import { memo, useMemo, useState } from 'react';
import { Link } from 'react-router';
import sortBy from 'sort-by';

import { CLASS_REP_GB_DISCOUNT_PERCENT } from 'my-core/class-rep-course-utils';
import { WIZE_SUBSCRIPTION_NAME } from 'my-core/constants';
import { approximateDuration, durationBetweenDates, formatDate, formatDateRange } from 'my-core/date-utils';
import { hasContent } from 'my-core/editor-utils';
import { instructorProfilePath, studentCoursePagePath } from 'my-core/routes';
import {
  DESCRIPTION_EDITOR_PLUGINS,
  getAttachmentTypes,
  getIsStudySessionExpired,
  getStudySessionDateDateStatus,
  getStudySessionDateLabel,
  getStudySessionIncludedFeatures,
  getStudySessionInstructor,
  getStudySessionPricing,
  isStudySessionUserRegistered,
  STUDY_SESSION_TYPES,
} from 'my-core/study-session-utils';
import { getIsActiveOrTrialing, getSubscriptionProductDiscountPercent } from 'my-core/subscription-utils';
import { getUserFirstName } from 'my-core/user-utils';
import { numberToCurrency, pluralize } from 'my-utils';

import { GradeBoosterPartner } from 'my-components/StudySession/GradeBoosterPartners';
import Editor, { LoadingFallback } from 'my-editor/index';
import MuiAvatar from 'my-elements/MuiAvatar';

import { GradeBoosterCardWrapper } from './StudentCoursePageCommon';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse, { collapseClasses } from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faChevronDown, faHourglassClock, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import bundleLongImg from 'images/product-illustrations/crash-course_long.svg';
import bundleSquareImg from 'images/product-illustrations/crash-course_square.svg';

export const StudySessionCard = memo(function StudySessionCard({ course, currentUser, studySession, subscription }) {
  return (
    <StudySessionCardWrapper
      course={course}
      currentUser={currentUser}
      studySession={studySession}
      subscription={subscription}
    />
  );
});

export const WeeklyBundleCard = memo(function StudySessionCard({ course, currentUser, subscription, weeklyBundle }) {
  return (
    <StudySessionCardWrapper
      course={course}
      currentUser={currentUser}
      studySession={weeklyBundle[0]}
      studySessions={weeklyBundle}
      subscription={subscription}
    />
  );
});

export const GradeBoosterBundleCard = memo(function GradeBoosterBundleCard({
  course,
  currentUser,
  onPurchase,
  studySessionBundle,
  subscription,
}) {
  const sessions = studySessionBundle.study_sessions;
  const discountPercent =
    currentUser.has_class_rep_discount ? CLASS_REP_GB_DISCOUNT_PERCENT : (
      getSubscriptionProductDiscountPercent(subscription)
    );
  const rate = (100 - discountPercent) / 100;
  const individualTotal = sessions.reduce((mem, ss) => mem + +(ss.regular_price || ss.price || 0), 0) * rate;
  const price = studySessionBundle.price * rate;
  const savings = Math.max(Math.round((individualTotal - price) * 100) / 100, 0);
  const priceCaption = [
    discountPercent && `${currentUser.has_class_rep_discount ? 'Class-Rep' : 'Member'} Price`,
    savings && `Save ${numberToCurrency(savings)}`,
  ]
    .filter(Boolean)
    .join(' - ');
  const sessionSummaries = useMemo(() => getBundleSessionSummaries(sessions, course?.id), [sessions, course?.id]);

  return (
    <GradeBoosterCardWrapper
      actions={
        <>
          <Typography
            sx={{
              textAlign: 'center',
              color: priceCaption ? 'orange.main' : 'text.primary',
              '& > .reg-price': { color: 'text.secondary', textDecoration: 'line-through' },
            }}
            variant="h6"
          >
            {!!savings && <span className="reg-price">{numberToCurrency(individualTotal)}</span>}
            {` ${numberToCurrency(price)}`}
          </Typography>
          {!!priceCaption && (
            <Typography sx={{ textAlign: 'center', color: 'orange.main', fontStyle: 'italic' }}>
              {priceCaption}
            </Typography>
          )}
          <Button fullWidth onClick={() => onPurchase(studySessionBundle.id)} sx={{ mt: 1 }} variant="contained">
            Purchase Bundle
          </Button>
        </>
      }
      borderColor="primary"
      image={bundleSquareImg}
      imageBanner={bundleLongImg}
      imageColor="orange"
      title={<Typography variant="h6">{studySessionBundle.title}</Typography>}
    >
      <Typography variant="body2">
        {hasContent(studySessionBundle.description) ?
          <Editor
            fallback={<LoadingFallback value={studySessionBundle.description} />}
            plugins={DESCRIPTION_EDITOR_PLUGINS}
            readOnly
            value={studySessionBundle.description}
          />
        : `Get all ${sessions.length} of these grade boosters together${savings ? ' and save' : ''}.`}
      </Typography>
      <ul
        css={({ spacing, typography }) => ({
          ...typography.body2,
          margin: 0,
          paddingLeft: spacing(2.5),
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(0.5),
        })}
      >
        {sessionSummaries.map(({ assessmentName, count, key, type }) => {
          const typeLabel = STUDY_SESSION_TYPES[type]?.label || type;
          return (
            <li key={key}>
              <strong>{count}</strong> {pluralize(count, [assessmentName, typeLabel].filter(Boolean).join(' '), true)}
            </li>
          );
        })}
      </ul>
    </GradeBoosterCardWrapper>
  );
});

function StudySessionCardWrapper({ course, currentUser, studySession, studySessions, subscription }) {
  const typeConfig = STUDY_SESSION_TYPES[studySession.type];

  const path = `${studentCoursePagePath(course)}/${typeConfig.slug}/${studySession.id}`;

  const studySessionUser = studySession.current_user_study_session_user;
  const isRegistered = isStudySessionUserRegistered(studySessionUser);
  const isExpired = getIsStudySessionExpired(studySession, studySessionUser);
  const instructor = getStudySessionInstructor(studySession);
  const inclusions = getStudySessionIncludedFeatures(studySessions || studySession);

  const discountPercent =
    currentUser.has_class_rep_discount ? CLASS_REP_GB_DISCOUNT_PERCENT : (
      getSubscriptionProductDiscountPercent(subscription)
    );

  return (
    <GradeBoosterCardWrapper
      actions={
        !isExpired &&
        (isRegistered ?
          <Button component={Link} fullWidth nativeButton={false} to={path} variant="outlined">
            View Details
          </Button>
        : renderPricingActions())
      }
      image={typeConfig.image}
      imageBanner={typeConfig.banner}
      imageColor={typeConfig.color}
      title={
        <>
          <Typography variant="h6">
            <Link to={path}>{studySession.title}</Link>
          </Typography>
          {studySession.partner_program && (
            <GradeBoosterPartner
              customPartner={studySession.custom_partner}
              partnerKey={studySession.partner_program}
            />
          )}
        </>
      }
    >
      {!isRegistered && <Typography>{typeConfig.shortDescription || typeConfig.description}</Typography>}
      {instructor && <InstructorInfo instructor={instructor} />}
      {!!inclusions.length && (
        <div
          css={({ spacing, typography }) => ({
            ...typography.body2,
            display: 'flex',
            alignItems: 'center',
            columnGap: spacing(2),
          })}
        >
          {inclusions.map(({ label }) => (
            <span key={label}>
              <FontAwesomeIcon icon={faCheck} />
              {` ${label}`}
            </span>
          ))}
        </div>
      )}
      <StudySessionDatesList studySession={studySession} studySessions={studySessions} />
    </GradeBoosterCardWrapper>
  );

  function renderPricingActions() {
    const isSubscriptionOffering = studySession.purchase_type === 'subscription';
    const [price, regularPrice] = getStudySessionPricing(studySession, discountPercent);
    const discountAmt = !!regularPrice && regularPrice - price;
    const discountStr =
      discountAmt &&
      (discountAmt > 20 ? numberToCurrency(discountAmt) : `${((discountAmt * 100) / regularPrice).toFixed()}%`);

    const discountType =
      currentUser.has_class_rep_discount ? 'Class-Rep'
      : discountPercent ? 'Member'
      : 'Sale';

    const hasActiveSubscription = isSubscriptionOffering && getIsActiveOrTrialing(subscription);
    const hasFreeAccess = isSubscriptionOffering ? hasActiveSubscription : !studySession.price;
    const hasFreePreview =
      !hasFreeAccess &&
      ((studySessions || [studySession]).some(
        ss =>
          ss.dates.some(
            ssd =>
              getStudySessionDateDateStatus(ssd, ss) === 'past' &&
              getAttachmentTypes(ss, ss.dates.length > 1 ? ssd.id : 'all').recordings.length,
          ) ||
          (!ss.dates.length && getAttachmentTypes(ss, 'all').recordings.length),
      ) ||
        studySessions?.some(ss => !ss.price));

    return (
      <>
        <Typography
          sx={{
            textAlign: 'center',
            color: regularPrice ? 'orange.main' : 'text.primary',
            '& > .reg-price': { color: 'text.secondary', textDecoration: 'line-through' },
          }}
          variant={isSubscriptionOffering ? 'subtitle1' : 'h6'}
        >
          {!!regularPrice && <span className="reg-price">{numberToCurrency(regularPrice)}</span>}
          {` ${
            price ? numberToCurrency(price)
            : isSubscriptionOffering ? `Included with ${WIZE_SUBSCRIPTION_NAME}`
            : 'Free Session'
          }`}
        </Typography>
        {!!regularPrice && !!price && (
          <Typography sx={{ textAlign: 'center', color: 'orange.main', fontStyle: 'italic' }}>
            {`${discountType} Price - ${discountStr} off`}
          </Typography>
        )}
        <div
          css={({ spacing }) => ({
            marginTop: spacing(1),
            display: 'flex',
            gap: spacing(1),
            flexWrap: 'wrap',
            '& > *': { flex: '1 1 100px' },
          })}
        >
          <Button component={Link} nativeButton={false} to={`${path}?checkout=true`} variant="contained">
            {price ?
              'Purchase'
            : isSubscriptionOffering ?
              hasActiveSubscription ?
                'Watch Now'
              : 'Subscribe'
            : 'Register Free'}
          </Button>
          {hasFreePreview && (
            <Button component={Link} nativeButton={false} to={path} variant="outlined">
              Free Preview
            </Button>
          )}
        </div>
      </>
    );
  }
}

function StudySessionDatesList({ studySession, studySessions }) {
  const [collapsed, setCollapsed] = useState(true);
  const dates = useMemo(
    () => (studySessions || [studySession]).flatMap(ss => ss.dates.map(ssd => [ss, ssd])).sort(sortBy('1.start_time')),
    [studySession, studySessions],
  );

  const activeDateIdx = useMemo(() => {
    if (dates.length <= 2) return;
    const curDate = new Date();
    const nextDateIdx = dates.findIndex(
      ([ss, ssd]) => new Date(ss.disable_zoom ? ssd.start_time : ssd.end_time) > curDate,
    );
    if (nextDateIdx === -1) return dates.length - 2;
    return nextDateIdx ? nextDateIdx - 1 : 0;
  }, [dates]);

  const partLabel = getStudySessionDateLabel(studySession);

  if (activeDateIdx == null) {
    return <div css={datesCss}>{dates.map(renderDate)}</div>;
  }

  return (
    <div css={datesCss}>
      {!!activeDateIdx && <Collapse in={!collapsed}>{dates.slice(0, activeDateIdx).map(renderDate)}</Collapse>}
      {dates.slice(activeDateIdx, activeDateIdx + 2).map((d, idx) => renderDate(d, idx + activeDateIdx))}
      {activeDateIdx < dates.length - 2 && (
        <Collapse in={!collapsed}>
          {dates.slice(activeDateIdx + 2).map((d, idx) => renderDate(d, idx + activeDateIdx + 2))}
        </Collapse>
      )}
      {dates.length > 1 && (
        <Button
          className="ssDatesToggleBtn"
          endIcon={<FontAwesomeIcon icon={faChevronDown} style={!collapsed && { transform: 'rotateX(180deg)' }} />}
          onClick={() => setCollapsed(!collapsed)}
          size="small"
        >
          {collapsed ? `Show All ${dates.length} ${partLabel}s` : 'Show Fewer'}
        </Button>
      )}
    </div>
  );

  function renderDate([ss, ssd], idx) {
    const [duration, timing] = studySessionDateDurationAndTiming(ss, ssd);
    return (
      <div key={ssd.id} className="ssDate">
        {dates.length > 1 && (
          <strong>
            {partLabel} {idx + 1}
          </strong>
        )}
        <div>
          <FontAwesomeIcon icon={faHourglassClock} /> {duration}
        </div>
        <span>|</span>
        <div>
          <FontAwesomeIcon icon={faCalendar} /> {timing}
        </div>
      </div>
    );
  }
}

const datesCss = ({ palette, spacing, typography }) => ({
  [`&, & .${collapseClasses.wrapperInner}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .ssDatesToggleBtn': {
    alignSelf: 'flex-start',
    '& svg': { transition: 'transform 0.5s', fontSize: '1em' },
  },
  '& .ssDate': {
    ...typography.body1,
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: spacing(1),
    alignItems: 'center',
    marginBottom: spacing(1),
    '& > strong': { flex: '0 0 68px' },
    '& svg': { color: palette.text.secondary },
  },
});
//
// function EngagementTypeLabel({ type }) {
//   const engagementTypeConfig = ENGAGEMENT_TYPE_CONFIG[type];
//   return (
//     <Label
//       color={engagementTypeConfig.color}
//       label={engagementTypeConfig.label}
//       sx={{ flexShrink: 0 }}
//       variant="light"
//     />
//   );
// }
// const ENGAGEMENT_TYPE_CONFIG = {
//   recording: { label: 'recorded', color: 'purple' },
//   live: { label: 'live online', color: 'primary' },
//   'on-demand': { label: 'on-demand', color: 'green' },
// };

function InstructorInfo({ instructor }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', '& .rating': { color: 'yellow.main' } }}>
      <MuiAvatar size={24} user={instructor} />
      <Typography
        {...(!instructor.hide_from_public && { component: Link, to: instructorProfilePath(instructor) })}
        noWrap
        variant="subtitle2"
      >
        {getUserFirstName(instructor)}
      </Typography>
      {instructor.instructor_stats?.feedbacks && (
        <>
          <FontAwesomeIcon className="rating" icon={faStar} />
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            {instructor.instructor_stats.feedbacks.average_rating.toFixed(1)}
          </Typography>
        </>
      )}
    </Box>
  );
}

const BUNDLE_SESSION_TYPE_ORDER = { weekly_tutorial: 0, exam_review: 1, exam_prep: 2 };

function getBundleSessionAssessment(ss, courseId) {
  if (ss.assessment) return { date: ss.assessment.date, id: ss.assessment.id, name: ss.assessment.name };
  const css =
    (courseId && ss.course_study_sessions?.find(c => c.course_id === courseId)) || ss.course_study_sessions?.[0];
  return { date: undefined, id: ss.assessment_id ?? css?.course_assessment_id, name: css?.assessment_name };
}

function getBundleSessionSummaries(sessions, courseId) {
  const groups = new Map();
  sessions.forEach(ss => {
    const { date: assessmentDate, id: assessmentId, name: assessmentName } = getBundleSessionAssessment(ss, courseId);
    const key = `${ss.type}:${assessmentId ?? assessmentName ?? ''}`;
    const count = ss.type === 'weekly_tutorial' ? ss.dates?.length || 1 : 1;
    const group = groups.get(key);
    if (group) group.count += count;
    else
      groups.set(key, {
        assessmentName,
        count,
        key,
        type: ss.type,
        _sk1:
          ss.type === 'weekly_tutorial' ? 0
          : assessmentDate ? new Date(assessmentDate).valueOf()
          : (assessmentId ?? Infinity),
        _sk2: BUNDLE_SESSION_TYPE_ORDER[ss.type] ?? 99,
      });
  });
  return [...groups.values()].sort(sortBy('_sk1', '_sk2'));
}

function studySessionDateDurationAndTiming(ss, ssd) {
  const startTime = new Date(ssd.start_time);
  const endTime = new Date(ssd.end_time);
  const isPast = startTime < new Date();

  const recordings = isPast && getAttachmentTypes(ss, ss.dates.length > 1 ? ssd.id : 'all').recordings;

  const prefix =
    ss.disable_zoom ?
      isPast && recordings?.length ?
        'Released: '
      : 'Releases: '
    : isPast ? 'Recorded: '
    : 'Live: ';

  const datePart =
    isPast ?
      ss.disable_zoom && !recordings?.length ?
        'Soon'
      : `${durationBetweenDates(startTime, null, { addSuffix: true })}`
    : `${formatDate(startTime, 'upcomingDateShort')}, ${ss.disable_zoom ? formatDate(startTime, 'time') : formatDateRange(startTime, endTime, 'time').replaceAll(' ', '')}`;

  const durationPart = pluralize(
    ...approximateDuration(
      recordings?.length ? recordings.reduce((mem, a) => mem + a.duration, 0) : (endTime - startTime) / 1000,
      { excludeDays: true, shortName: true },
    ),
  );

  return [durationPart, [prefix, datePart].filter(Boolean).join('')];
}
